import type { Ad } from "@/lib/data";

const KEY = "albazar:recent";
const MAX = 30;

type Seen = { id: string; category: string; city?: string; price?: number; at: number };

function read(): Seen[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? (list as Seen[]) : [];
  } catch {
    return [];
  }
}

export function getRecentlyViewed(): Seen[] {
  return read();
}

export function rememberViewed(ad: Pick<Ad, "id" | "category" | "city" | "price">) {
  if (typeof window === "undefined") return;
  const list = read().filter((s) => s.id !== String(ad.id));
  list.unshift({
    id: String(ad.id),
    category: ad.category,
    city: ad.city,
    price: ad.price,
    at: Date.now(),
  });
  try {
    window.localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX)));
  } catch {
    /* kvota */
  }
}

/** Baxılanlara və seçilmişlərə əsasən ən uyğun elanları qaytarır. */
export function recommend(all: Ad[], opts: { exclude?: string[]; limit?: number } = {}): Ad[] {
  const seen = read();
  const exclude = new Set([...(opts.exclude ?? []), ...seen.map((s) => s.id)]);
  const weights = new Map<string, number>();
  const cityWeights = new Map<string, number>();
  seen.forEach((s, i) => {
    const w = Math.max(1, MAX - i);
    weights.set(s.category, (weights.get(s.category) ?? 0) + w);
    if (s.city) cityWeights.set(s.city, (cityWeights.get(s.city) ?? 0) + w);
  });
  const scored = all
    .filter((a) => a.status === "active" && !exclude.has(String(a.id)))
    .map((a) => {
      let score = (weights.get(a.category) ?? 0) * 3 + (cityWeights.get(a.city) ?? 0);
      if (a.badge === "vip") score += 8;
      if (a.badge === "premium") score += 6;
      score += Math.max(0, 5 - (Date.now() - +new Date(a.createdAt)) / 86400000);
      return { a, score };
    })
    .sort((x, y) => y.score - x.score);
  return scored.slice(0, opts.limit ?? 8).map((s) => s.a);
}

/** Eyni kateqoriya və yaxın qiymətli oxşar elanlar. */
export function similarAds(all: Ad[], ad: Ad, limit = 8): Ad[] {
  return all
    .filter((a) => String(a.id) !== String(ad.id) && a.status === "active")
    .map((a) => {
      let score = 0;
      if (a.category === ad.category) score += 10;
      if (a.city === ad.city) score += 3;
      const diff = Math.abs(a.price - ad.price) / Math.max(1, ad.price);
      score += Math.max(0, 5 - diff * 5);
      return { a, score };
    })
    .filter((s) => s.score > 3)
    .sort((x, y) => y.score - x.score)
    .slice(0, limit)
    .map((s) => s.a);
}
